import { Messages } from '~src/codegen/Localize';

import { Domain, DomainID } from '../types/Domain';
import { DomainPermission } from '../types/DomainPermission';
import { DomainTableItem } from '../types/DomainTableItem';
import { DomainValidationStatus } from '../types/DomainValidationStatus';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';
import { PoolModel, UserDefinedPoolModel } from '../types/PoolModel';

export function getDomainFromDomainID(pools: Record<string, PoolModel>, blockedDomains: Record<string, Domain>, domainID: DomainID | undefined): Domain | undefined {
  if (domainID === null) {
    return undefined;
  }

  for (const pool of Object.values(pools)) {
    const domain = pool.domains[domainID];
    if (domain !== undefined) {
      return {
        ...domain,
        domainPermission: DomainPermission.ALLOW
      };
    }
  }
  for (const domain of Object.values(blockedDomains)) {
    if (domain.id === domainID) {
      return {
        ...domain,
        domainPermission: DomainPermission.BLOCK
      };
    }
  }

  return undefined;
}

export function createDomainTableItems(userDefinedPools: Record<string, UserDefinedPoolModel>, blockedDomains: Record<string, Domain>) {
  const items: DomainTableItem[] = [];
  for (const pool of Object.values(userDefinedPools)) {
    for (const domain of Object.values(pool.domains)) {
      items.push({
        domainId: domain.id,
        domainName: domain.domainName,
        poolName: domain.domainPermission === DomainPermission.BLOCK ? Messages.bridgeSiteSettingsBlocked() : pool.displayName,
        poolId: (domain.domainPermission === DomainPermission.BLOCK ? SystemDefinedPoolID.Blocked : pool.id) as PoolID,
        domainPermission: DomainPermission.ALLOW
      });
    }
  }
  for (const domain of Object.values(blockedDomains)) {
    items.push({
      domainId: domain.id,
      domainName: domain.domainName,
      poolName: Messages.bridgeSiteSettingsBlocked(),
      poolId: SystemDefinedPoolID.Blocked,
      domainPermission: DomainPermission.BLOCK
    });
  }

  return items;
}

export function getPoolIDForDomainID(pools: Record<string, PoolModel>, blockedDomains: Record<string, Domain>, domainID: DomainID | undefined): PoolID | undefined {
  if (domainID === null) {
    return undefined;
  }

  for (const pool of Object.values(pools)) {
    const domain = pool.domains[domainID];
    if (domain !== undefined) {
      return pool.id;
    }
  }

  const blockedDomain = blockedDomains[domainID];
  if (blockedDomain !== undefined) {
    return SystemDefinedPoolID.Blocked as PoolID;
  }

  return undefined;
}

const DOMAIN_MAX_CHARACTERS = 253;
const SPECIAL_CHARS_REGEX = new RegExp('[^A-Za-z0-9-_\\.\\*/]');
const LABEL_REGEX = '[A-Za-z0-9-_]{1,63}';
const URI_REGEX = '(' + LABEL_REGEX + '\\.)*' + LABEL_REGEX + '(\\.?)';
const DNS_DOMAIN_REGEX = new RegExp('^(\\*\\.)?' + URI_REGEX + '$');

const IPV4_BLOCK_REGEX = '[0-9]{1,3}';
const IPV4_EXACT_REGEX = '(' + IPV4_BLOCK_REGEX + '\\.){3}(' + IPV4_BLOCK_REGEX + ')';
const IPV4_STANDARD_REGEX = new RegExp('^(' + IPV4_EXACT_REGEX + ')$');
const IPV4_BITMASK_REGEX = new RegExp('^(' + IPV4_EXACT_REGEX + '/[0-9]{1,2})$');

export function validateDomainName(changedDomainName: string, otherDomainNames: ReadonlyArray<string>): DomainValidationStatus {
  if (changedDomainName.length === 0) {
    return DomainValidationStatus.NO_ERROR;
  }

  changedDomainName = changedDomainName.toLowerCase();
  const wildcardIndex = changedDomainName.indexOf('*');

  if (changedDomainName === Messages.bridgeSiteSettingsDefault().toLowerCase()) {
    return DomainValidationStatus.DOMAIN_NAME_IS_DEFAULT;
  }
  if (changedDomainName === Messages.bridgeSiteSettingsBlocked().toLowerCase()) {
    return DomainValidationStatus.DOMAIN_NAME_IS_BLOCKED;
  }
  if (changedDomainName.length >= DOMAIN_MAX_CHARACTERS) {
    return DomainValidationStatus.DOMAIN_NAME_MAXIMUM_CHARACTERS;
  }
  if (wildcardIndex !== -1) {
    if (wildcardIndex !== 0) {
      return DomainValidationStatus.WILDCARD_NOT_LEFTMOST_CHARACTER_ERROR;
    } else if (changedDomainName.charAt(1) !== '.') {
      return DomainValidationStatus.WILDCARD_PERIOD_ERROR;
    }
  }
  if (SPECIAL_CHARS_REGEX.test(changedDomainName)) {
    return DomainValidationStatus.INVALID_CHARACTERS_ERROR;
  }
  if (domainContainsInvalidDashes(changedDomainName)) {
    return DomainValidationStatus.INVALID_DASHES_ERROR;
  }
  if (domainContainsInvalidUnderscores(changedDomainName)) {
    return DomainValidationStatus.INVALID_UNDERSCORES_ERROR;
  }
  if (!isValidDomainFormat(changedDomainName)) {
    return DomainValidationStatus.INVALID_FORMAT_ERROR;
  }
  if (otherDomainNames.includes(changedDomainName)) {
    return DomainValidationStatus.NOT_UNIQUE_ERROR;
  }
  return DomainValidationStatus.NO_ERROR;
}

function isValidDomainFormat(domainName: string) {
  return IPV4_BITMASK_REGEX.test(domainName) || IPV4_STANDARD_REGEX.test(domainName) || DNS_DOMAIN_REGEX.test(domainName);
}

function domainContainsInvalidDashes(domainName: string) {
  const subDomains = domainName.split('.');
  for (const subDomain of subDomains) {
    if (subDomain[0] === '-' || subDomain[subDomain.length - 1] === '-') {
      return true;
    }
  }
  return false;
}

function domainContainsInvalidUnderscores(domainName: string) {
  const subDomains = domainName.split('.');
  for (const subDomain of subDomains) {
    if (subDomain[0] === '_' || subDomain[subDomain.length - 1] === '_') {
      return true;
    }
  }
  return false;
}
